import React, { useContext, useMemo } from 'react';
import { Typography, List, ListItem, ListItemAvatar, ListItemText } from '@mui/material';
import { AuthContext } from '../../auth/Auth';
import { firebaseHelper, useTeams, usePermissions } from 'ui';
import { CardTypes, ResourceType, TeamsInterface } from 'ts-interfaces';
import { MemberAvatar } from '../share_resource/components/MemberAvatar';
import { getDisplayName } from '../share_resource/utils/ShareResourceUtils';
import { ShareResourceModal } from '../share_resource/components/ShareResourceModal';
import { useTeamsProAccess } from '../../hooks/useTeamsProAccess';



type AccessType = 'owner' | 'editor' | 'viewer' | null;
interface TeamMemberWithAccess extends TeamsInterface { access: AccessType; }

interface PeopleWithAccessProps {
  resourceId: string; 
  resourceName: string; 
  resourceType: CardTypes;
  resourceOwner: string; 
  teamID?: string; // Keep as optional for backward compatibility
  hasViewerAccess?: boolean;
  isTeamsProUser?: boolean;
  isRegularProUser?: boolean;
}

export const PeopleWithAccess: React.FC<PeopleWithAccessProps> = ({ 
  resourceId, resourceName, resourceType, resourceOwner, teamID, hasViewerAccess = false, isTeamsProUser, isRegularProUser
}) => {
  const { currentUser } = useContext(AuthContext);
  const permissions = usePermissions(currentUser?.uid, firebaseHelper.db);
  
  // Use teamID if provided, otherwise get designFirmID from permissions
  const designFirmID = teamID || permissions?.permissions?.teamsDesigner?.[0];
  const { teams } = useTeams(designFirmID, firebaseHelper.db);
    // const { isTeamsProUser, isRegularProUser } = useTeamsProAccess();

  const getResourceType = (type: CardTypes): ResourceType => ({
    [CardTypes.Projects]: ResourceType.Projects,
    [CardTypes.Boards]: ResourceType.Boards,
    [CardTypes.Collections]: ResourceType.Collections
  }[type] || ResourceType.Boards);

  // Use the same resource keys structure as ShareResourceModal
  const resourceKeys = {
    [CardTypes.Collections]: { read: 'collectionRead', write: 'collectionWrite' },
    [CardTypes.Projects]: { read: 'projectRead', write: 'projectWrite' },
    [CardTypes.Boards]: { read: 'boardRead', write: 'boardWrite' }
  }[resourceType];

  // Use the same filtering logic as ShareResourceModal but include all users for display
  const collaborators: TeamMemberWithAccess[] = useMemo(() => {
    if (!teams?.length) return [];
    
    let filteredTeams = teams
      .filter(m => m?.uid && (m.uid === resourceOwner || 
        m.permissions?.[resourceKeys.read]?.includes(resourceId) ||
        m.permissions?.[resourceKeys.write]?.includes(resourceId)));

    // For regular PRO users, only show the owner (no old shared access users)
    if (isRegularProUser === true && isTeamsProUser !== true) {
      filteredTeams = filteredTeams.filter(m => m.uid === resourceOwner);
    }

    return filteredTeams
      .map(m => ({
        ...m,
        access: m.uid === resourceOwner ? 'owner' :
               m.permissions?.[resourceKeys.write]?.includes(resourceId) ? 'editor' : 'viewer'
      }))
      .sort((a, b) => a.access === 'owner' ? -1 : b.access === 'owner' ? 1 : 0);
  }, [teams, resourceId, resourceKeys, resourceOwner, isTeamsProUser, isRegularProUser]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexShrink: 0 }}>
        <Typography variant="h6" style={{ fontWeight: 'medium' }}>People with Access</Typography>
        {!hasViewerAccess && <ShareResourceModal resourceID={resourceId} type={getResourceType(resourceType)} title={resourceName} owner={resourceOwner} />}
      </div>      
      
      <List style={{ 
        width: '100%', 
        padding: 0, 
        flex: 1,
        overflowY: 'auto',
        minHeight: 0,
      }}>
        {collaborators.map((user) => {
          const displayName = getDisplayName(user) + (currentUser?.uid === user.uid ? ' (You)' : '');
          const role = user.access ? user.access.charAt(0).toUpperCase() + user.access.slice(1) : '';
          
          return (
            <ListItem key={user.id} disablePadding style={{ paddingTop: '4px', paddingBottom: '4px', paddingLeft: '8px', paddingRight: '8px' }}>
              <ListItemAvatar><MemberAvatar member={user} /></ListItemAvatar>
              <ListItemText 
                primary={displayName} secondary={user.email}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium', noWrap: true, overflow: 'hidden', textOverflow: 'ellipsis' }}
                secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary', noWrap: true, overflow: 'hidden', textOverflow: 'ellipsis' }}
                style={{ maxWidth: 'calc(100% - 140px)', flex: 1, marginRight: '16px' }}
              />
              <div style={{ flex: '0 0 auto', marginLeft: 'auto' }}>
                <Typography variant="caption" color="text.secondary" style={{ textTransform: 'none', fontWeight: 'medium' }}>
                  {role}
                </Typography>
              </div>
            </ListItem>
          );
        })}
      </List>
    </div>
  );
};